import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Wrench,
  AlertTriangle,
  ClipboardList,
  ShieldAlert,
  Boxes,
  BarChart3,
  PenTool,
  ArrowRight,
  Loader2,
  Radio,
} from "lucide-react";
import SwingProgressTracker from "@/components/SwingProgressTracker";

/**
 * Enterprise dashboard — one-click actions across the top, live counts below,
 * and a "latest activity" strip. Everything reads from the production backend.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  const [recent, setRecent] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      // Counts
      const countCalls = [
        ["breakdowns_pending", "/breakdowns/pending-signoff/count"],
        ["hazards_pending", "/hazards/pending/count"],
        ["inspections_queue", "/inspections/queue/count"],
        ["ppfs_open", "/ppfs/count-open"],
      ];
      const nextCounts = {};
      await Promise.all(
        countCalls.map(async ([key, path]) => {
          try {
            const { data } = await api.get(path);
            nextCounts[key] = extractCount(data);
          } catch {
            nextCounts[key] = null;
          }
        })
      );
      if (alive) setCounts(nextCounts);

      // Recent lists (kept short)
      const nextRecent = {};
      const listCalls = [
        ["handovers", "/handovers"],
        ["breakdowns", "/breakdowns"],
        ["inspections", "/inspections"],
      ];
      await Promise.all(
        listCalls.map(async ([key, path]) => {
          try {
            const { data } = await api.get(path);
            const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
            nextRecent[key] = arr.slice(0, 4);
          } catch (ex) {
            nextRecent[key] = { error: apiError(ex) };
          }
        })
      );
      if (alive) setRecent(nextRecent);
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero */}
      <div className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            <Radio className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
            Live · field-maint.emergent.host
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mt-2">
            {greeting()},<br className="hidden md:block" />
            <span style={{ color: "var(--primary)" }}>
              {user?.name?.split(" ")?.[0] || user?.username || "Operator"}.
            </span>
          </h1>
          <p className="text-muted mt-3 max-w-xl">
            One-tap access to every Pit Link resource. Sign off handovers, breakdowns and Planning Requests right here.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <div className="pill pill-orange" data-testid="user-role-pill">
            {user?.role || "user"}
          </div>
          {user?.site_code && <div className="pill">{user.site_code}</div>}
        </div>
      </div>

      {/* One-click actions */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted mb-3">
          Quick actions
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickCard
            to="/insights"
            label="Performance"
            desc="Live graphs, download PDF"
            icon={BarChart3}
            testid="quick-insights"
            featured
          />
          <QuickCard
            to="/handovers"
            label="Handovers"
            desc="Sign off & manage"
            icon={ClipboardList}
            testid="quick-handovers"
          />
          <QuickCard
            to="/breakdowns"
            label="Breakdowns"
            desc={counts.breakdowns_pending ? `${counts.breakdowns_pending} pending sign-off` : "Approve & sign"}
            icon={Wrench}
            testid="quick-breakdowns"
            badge={counts.breakdowns_pending}
          />
          <QuickCard
            to="/ppfs"
            label="Planning Requests"
            desc={counts.ppfs_open ? `${counts.ppfs_open} open` : "Complete & sign"}
            icon={PenTool}
            testid="quick-ppfs"
            badge={counts.ppfs_open}
          />
        </div>
      </div>

      {/* Live counts */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted mb-3">
          Live counts
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile label="Breakdowns · Pending" value={counts.breakdowns_pending} icon={Wrench} to="/breakdowns" testid="kpi-breakdowns" />
          <Tile label="Hazards · Pending" value={counts.hazards_pending} icon={ShieldAlert} tone="danger" to="/hazards" testid="kpi-hazards" />
          <Tile label="Inspections · Queue" value={counts.inspections_queue} icon={ClipboardList} tone="hazard" to="/inspections" testid="kpi-inspections" />
          <Tile label="Open Planning Requests" value={counts.ppfs_open} icon={AlertTriangle} to="/ppfs" testid="kpi-ppfs" />
        </div>
      </div>

      {/* Live swing progress */}
      <SwingProgressTracker />

      {/* Recent */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted mb-3">
          Latest activity
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <RecentPanel title="Operational Handover" to="/handovers" items={recent.handovers}
            renderItem={(it) => ({
              title: it.summary || it.site_code || "Handover",
              sub: `${it.shift || ""} · ${it.created_by_name || ""}`,
              when: it.created_at,
            })} />
          <RecentPanel title="Asset Breakdowns" to="/breakdowns" items={recent.breakdowns}
            renderItem={(it) => ({
              title: it.title || it.description || it.asset_name || "Breakdown",
              sub: `${it.asset_name || ""} · ${it.status || ""}`,
              when: it.created_at,
            })} />
          <RecentPanel title="Inspections" to="/inspections" items={recent.inspections}
            renderItem={(it) => ({
              title: it.template_name || it.title || "Inspection",
              sub: `${it.asset_name || ""} · ${it.status || ""}`,
              when: it.created_at,
            })} />
        </div>
      </div>

      <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
        <Link to="/assets" className="btn btn-ghost btn-sm" data-testid="link-assets">
          <Boxes className="w-4 h-4" /> Asset registry
        </Link>
        <Link to="/hazards" className="btn btn-ghost btn-sm" data-testid="link-hazards">
          <ShieldAlert className="w-4 h-4" /> Hazards
        </Link>
      </div>
    </div>
  );
}

function QuickCard({ to, label, desc, icon: Icon, testid, featured, badge }) {
  return (
    <Link
      to={to}
      className={featured ? "panel-primary p-5 md:p-6 flex flex-col justify-between min-h-[150px]" : "panel-heavy p-5 md:p-6 flex flex-col justify-between min-h-[150px]"}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={
            featured
              ? { background: "#0a0a0a", color: "var(--primary)" }
              : { background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }
          }
        >
          <Icon className="w-5 h-5" />
        </div>
        <ArrowRight className={featured ? "w-5 h-5" : "w-5 h-5 text-muted"} />
      </div>
      <div>
        <div className="font-display font-black text-2xl leading-none tracking-tight">
          {label}
        </div>
        <div className={`text-sm mt-1 ${featured ? "text-black/70" : "text-muted"}`}>{desc}</div>
        {badge > 0 && !featured && (
          <span className="pill pill-orange-solid mt-3 inline-flex">{badge}</span>
        )}
      </div>
    </Link>
  );
}

function Tile({ label, value, icon: Icon, tone, to, testid }) {
  const styles =
    tone === "hazard"
      ? { background: "linear-gradient(180deg, #ffb15c, #f58220)", color: "#0a0a0a", borderColor: "#f58220" }
      : tone === "danger"
      ? { background: "linear-gradient(180deg, #ef4444, #b91c1c)", color: "#fff", borderColor: "#ef4444" }
      : {};
  const iconStyle =
    tone === "hazard"
      ? { background: "#0a0a0a", color: "var(--primary)" }
      : tone === "danger"
      ? { background: "#fff", color: "var(--danger)" }
      : { background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" };
  const body = (
    <div className="panel p-5 md:p-6 min-h-[150px] flex flex-col justify-between" style={styles} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] font-black">{label}</div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={iconStyle}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="font-display font-black text-5xl leading-none tracking-tight">
        {value == null ? <Loader2 className="w-6 h-6 animate-spin opacity-60" /> : value}
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function RecentPanel({ title, items, renderItem, to }) {
  return (
    <div className="panel-heavy p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-black uppercase tracking-widest text-sm">{title}</div>
        {to && (
          <Link to={to} className="text-[11px] uppercase tracking-widest text-muted hover:text-white">
            All <ArrowRight className="inline w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {!items ? (
        <div className="flex items-center gap-2 text-muted text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items?.error ? (
        <div className="pill pill-red !text-xs">{items.error}</div>
      ) : items.length === 0 ? (
        <div className="text-muted text-sm py-3">Nothing yet.</div>
      ) : (
        <div className="divide-border">
          {items.map((it, i) => {
            const r = renderItem(it);
            return (
              <div key={it.id || i} className="py-3 first:pt-0">
                <div className="font-display font-black text-sm truncate">{r.title}</div>
                {r.sub && <div className="text-xs text-muted truncate">{r.sub}</div>}
                {r.when && <div className="text-[10px] font-mono-tight text-muted mt-0.5">{tryDate(r.when)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function extractCount(d) {
  if (typeof d === "number") return d;
  if (typeof d === "object" && d) return d.count ?? d.total ?? d.pending ?? d.value ?? Object.values(d)[0];
  return null;
}
function tryDate(v) { try { return new Date(v).toLocaleString(); } catch { return String(v); } }
